import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Zap, UserPlus, ShoppingCart, AlertTriangle, Pencil } from 'lucide-react';
import { API, cardCls, txtMain, txtSub } from './nlShared';
import { LoadingSpinner } from '../core';
import { toast } from 'sonner';

const ICONS = { welcome: UserPlus, cart: ShoppingCart, expired: AlertTriangle };
const DELAY_PRESETS = [[1, '1 h'], [24, '24 h'], [48, '2 j'], [72, '3 j'], [168, '7 j']];

export function NlAutomations({ theme, onEditCampaign }) {
  const light = theme === 'light';
  const [autos, setAutos] = useState(null);

  const load = () => axios.get(`${API}/admin/nl/automations`, { withCredentials: true }).then(r => setAutos(r.data)).catch(() => setAutos([]));
  useEffect(() => { load(); }, []);

  const patch = async (trigger, body) => {
    try {
      await axios.put(`${API}/admin/nl/automations/${trigger}`, body, { withCredentials: true });
      setAutos(a => a.map(x => x.trigger === trigger ? { ...x, ...body } : x));
      if ('enabled' in body) toast.success(body.enabled ? 'Automatisation activée ✅' : 'Automatisation désactivée');
    } catch { toast.error('Erreur de sauvegarde'); }
  };

  if (!autos) return <LoadingSpinner />;

  return (
    <div data-testid="nl-automations">
      <p className={`text-xs mb-4 ${txtSub(light)}`}>
        <Zap size={12} className="inline mr-1 text-[#E9641A]" />
        Les scénarios s'exécutent automatiquement : chaque utilisateur ne reçoit l'email qu'une seule fois.
        À l'activation, les utilisateurs déjà concernés sont ignorés (seuls les nouveaux cas déclenchent l'envoi).
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {autos.map(a => {
          const Icon = ICONS[a.trigger] || Zap;
          return (
            <div key={a.trigger} className={`${cardCls(light)} p-5 flex flex-col`} data-testid={`nl-auto-${a.trigger}`}>
              <div className="flex items-start justify-between mb-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.enabled ? 'bg-[#E9641A]/15' : light ? 'bg-gray-100' : 'bg-white/5'}`}>
                  <Icon size={18} className={a.enabled ? 'text-[#E9641A]' : txtSub(light)} />
                </span>
                <button data-testid={`nl-auto-toggle-${a.trigger}`} onClick={() => patch(a.trigger, { enabled: !a.enabled })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${a.enabled ? 'bg-[#E9641A]' : light ? 'bg-gray-200' : 'bg-white/10'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${a.enabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
              <p className={`text-sm font-bold ${txtMain(light)}`}>{a.label}</p>
              <p className={`text-xs mt-1 flex-1 ${txtSub(light)}`}>{a.desc}</p>
              <p className={`text-[11px] mt-2 truncate ${txtSub(light)}`}>Objet : <span className={txtMain(light)}>{a.subject || '—'}</span></p>

              {a.has_delay && (
                <div className="mt-3">
                  <p className={`text-[10px] font-bold uppercase mb-1.5 ${txtSub(light)}`}>Délai après l'événement</p>
                  <div className="flex flex-wrap gap-1.5">
                    {DELAY_PRESETS.map(([h, lbl]) => (
                      <button key={h} data-testid={`nl-auto-delay-${a.trigger}-${h}`} onClick={() => patch(a.trigger, { delay_hours: h })}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${a.delay_hours === h ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-500' : 'bg-white/8 text-white/50'}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              )}
              {!a.has_delay && <p className={`text-[10px] mt-3 uppercase font-bold ${txtSub(light)}`}>Envoi immédiat à la détection</p>}

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                <span className={`text-xs ${txtSub(light)}`}><b className="text-[#E9641A]">{a.sent_count}</b> envoyé{a.sent_count > 1 ? 's' : ''}</span>
                <button data-testid={`nl-auto-edit-${a.trigger}`} onClick={() => onEditCampaign(a.campaign_id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/8 text-white hover:bg-white/15'}`}>
                  <Pencil size={12} /> Modifier l'email
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
